import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../config/supabase';
import { useAuth } from '../contexts/AuthContext';
import { notifyTaskAssigned } from '../utils/notifications/triggers';
import { useEditGuard } from './useEditGuard';

// ============================================
// INTERFACES
// ============================================

export interface KanbanColumn {
  id: string;
  name: string;
  position: number;
  is_done_column: boolean;
  is_no_section_column: boolean;
  space_id: string | null;
  user_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface Todo {
  id: string;
  column_id: string;
  space_id: string | null;
  user_id: string | null;
  title: string;
  notes: string | null;
  due_date: string | null; // YYYY-MM-DD
  due_time: string | null; // HH:mm
  duration: number | null; // minutes
  reminder_minutes_before: number | null; // null = no reminder
  reminder_fire_at: string | null;        // UTC ISO when notification should fire
  reminder_sent_at: string | null;        // set after notification is delivered
  assignee_id: string | null;
  calendar_id: string | null;
  priority: 'none' | 'low' | 'medium' | 'high';
  position: number;
  archived_at: string | null;
  completed_at: string | null;
  // Null once the author deletes their account: the task stays on the shared
  // board, its authorship does not.
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface AutoArchiveSettings {
  enabled: boolean;
  days: number;
  enabledAt: string | null; // only archive todos updated after this timestamp
}

interface UseSupabaseKanbanOptions {
  spaceId?: string | null; // null = personal space, undefined = not filtering
  autoArchive?: AutoArchiveSettings;
}

// ============================================
// HOOK
// ============================================

export function useSupabaseKanban(options: UseSupabaseKanbanOptions = {}) {
  const allowEdit = useEditGuard();
  const { spaceId, autoArchive } = options;
  const { user } = useAuth();

  const [columns, setColumns] = useState<KanbanColumn[]>([]);
  const [todos, setTodos] = useState<Todo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Track IDs we've already mutated optimistically so realtime echo doesn't cause a second render
  const pendingOpsRef = useRef<Set<string>>(new Set());

  // ============================================
  // FETCH DATA
  // ============================================

  const sortColumnsNoSectionFirst = (cols: KanbanColumn[]): KanbanColumn[] => {
    return [
      ...cols.filter(c => c.is_no_section_column),
      ...cols.filter(c => !c.is_no_section_column).sort((a, b) => a.position - b.position),
    ];
  };

  const fetchColumns = useCallback(async (): Promise<KanbanColumn[]> => {
    if (!user) return [];

    try {
      let query = supabase
        .from('kanban_columns')
        .select('*')
        .order('position', { ascending: true });

      if (spaceId === null) {
        // Personal space
        query = query.is('space_id', null).eq('user_id', user.id);
      } else if (spaceId !== undefined) {
        // Shared space
        query = query.eq('space_id', spaceId);
      } else {
        // Default to personal
        query = query.is('space_id', null).eq('user_id', user.id);
      }

      const { data, error } = await query;

      if (error) throw error;

      // If no columns exist, create defaults
      if (!data || data.length === 0) {
        const { data: newColumns, error: createError } = await supabase.rpc(
          'create_default_kanban_columns',
          {
            p_space_id: spaceId || null,
            p_user_id: spaceId ? null : user.id,
          }
        );

        if (createError) throw createError;
        const cols: KanbanColumn[] = newColumns || [];
        // Ensure a No Section column exists
        if (!cols.some(c => c.is_no_section_column)) {
          const { data: noSection } = await supabase
            .from('kanban_columns')
            .insert({
              name: 'No group',
              position: -1,
              is_done_column: false,
              is_no_section_column: true,
              space_id: spaceId || null,
              user_id: spaceId ? null : user.id,
            })
            .select()
            .single();
          if (noSection) cols.unshift(noSection);
        }
        return sortColumnsNoSectionFirst(cols);
      }

      // Ensure a No Section column exists for existing users
      if (!data.some(c => c.is_no_section_column)) {
        const { data: noSection } = await supabase
          .from('kanban_columns')
          .insert({
            name: 'No group',
            position: -1,
            is_done_column: false,
            is_no_section_column: true,
            space_id: spaceId || null,
            user_id: spaceId ? null : user.id,
          })
          .select()
          .single();
        if (noSection) return sortColumnsNoSectionFirst([noSection, ...data]);
      }

      return sortColumnsNoSectionFirst(data);
    } catch (err) {
      console.error('Error fetching columns:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
      return [];
    }
  }, [user, spaceId]);

  const fetchTodos = useCallback(async (): Promise<Todo[]> => {
    if (!user) return [];

    try {
      let query = supabase
        .from('todos')
        .select('*')
        // Fetch ALL todos including archived - filtering happens in UI
        .order('position', { ascending: true });

      if (spaceId === null) {
        // Personal space
        query = query.is('space_id', null).eq('user_id', user.id);
      } else if (spaceId !== undefined) {
        // Shared space
        query = query.eq('space_id', spaceId);
      } else {
        // Default to personal
        query = query.is('space_id', null).eq('user_id', user.id);
      }

      const { data, error } = await query;

      if (error) throw error;

      return data || [];
    } catch (err) {
      console.error('Error fetching todos:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
      return [];
    }
  }, [user, spaceId]);

  const fetchAll = useCallback(async () => {
    if (!user) {
      setColumns([]);
      setTodos([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const [fetchedColumns, fetchedTodos] = await Promise.all([
        fetchColumns(),
        fetchTodos(),
      ]);
      setColumns(fetchedColumns);
      setTodos(fetchedTodos);
    } finally {
      setLoading(false);
    }
  }, [user, fetchColumns, fetchTodos]);

  // ============================================
  // COLUMN OPERATIONS
  // ============================================

  const addColumn = useCallback(
    async (name: string): Promise<KanbanColumn | null> => {
      if (!user) return null;
      if (!allowEdit()) return null;

      try {
        // Get max position
        const maxPosition = columns.length > 0
          ? Math.max(...columns.map((c) => c.position))
          : -1;

        const columnData = {
          name,
          position: maxPosition + 1,
          is_done_column: false,
          space_id: spaceId || null,
          user_id: spaceId ? null : user.id,
        };

        const { data, error } = await supabase
          .from('kanban_columns')
          .insert([columnData])
          .select()
          .single();

        if (error) throw error;

        setColumns((current) => [...current, data]);
        return data;
      } catch (err) {
        console.error('Error adding column:', err);
        setError(err instanceof Error ? err.message : 'Unknown error');
        return null;
      }
    },
    [user, spaceId, columns]
  );

  const updateColumn = useCallback(
    async (id: string, updates: Partial<Pick<KanbanColumn, 'name' | 'is_done_column' | 'is_no_section_column'>>): Promise<boolean> => {
      if (!user) return false;
      if (!allowEdit()) return false;

      try {
        const { error } = await supabase
          .from('kanban_columns')
          .update({ ...updates, updated_at: new Date().toISOString() })
          .eq('id', id);

        if (error) throw error;

        setColumns((current) =>
          current.map((col) => (col.id === id ? { ...col, ...updates } : col))
        );
        return true;
      } catch (err) {
        console.error('Error updating column:', err);
        setError(err instanceof Error ? err.message : 'Unknown error');
        return false;
      }
    },
    [user]
  );

  const deleteColumn = useCallback(
    async (id: string): Promise<boolean> => {
      if (!user) return false;
      if (!allowEdit()) return false;

      try {
        const { error } = await supabase
          .from('kanban_columns')
          .delete()
          .eq('id', id);

        if (error) throw error;

        // Remove column and its todos from state
        setColumns((current) => current.filter((col) => col.id !== id));
        setTodos((current) => current.filter((todo) => todo.column_id !== id));
        return true;
      } catch (err) {
        console.error('Error deleting column:', err);
        setError(err instanceof Error ? err.message : 'Unknown error');
        return false;
      }
    },
    [user]
  );

  const reorderColumns = useCallback(
    async (columnIds: string[]): Promise<boolean> => {
      if (!user) return false;
      if (!allowEdit()) return false;

      // Optimistic update
      const reorderedColumns = columnIds
        .map((id, index) => {
          const col = columns.find((c) => c.id === id);
          return col ? { ...col, position: index } : null;
        })
        .filter(Boolean) as KanbanColumn[];

      setColumns(reorderedColumns);

      try {
        // Update each column's position in the database
        const updates = columnIds.map((id, index) =>
          supabase
            .from('kanban_columns')
            .update({ position: index, updated_at: new Date().toISOString() })
            .eq('id', id)
        );

        await Promise.all(updates);
        return true;
      } catch (err) {
        console.error('Error reordering columns:', err);
        setError(err instanceof Error ? err.message : 'Unknown error');
        // Revert on error
        await fetchColumns().then(setColumns);
        return false;
      }
    },
    [user, columns, fetchColumns]
  );

  // ============================================
  // TODO OPERATIONS
  // ============================================

  const addTodo = useCallback(
    async (
      todo: Pick<Todo, 'column_id' | 'title'> & Partial<Omit<Todo, 'id' | 'created_at' | 'updated_at' | 'created_by'>>,
      onOptimisticId?: (tempId: string) => void
    ): Promise<Todo | null> => {
      if (!user) return null;
      if (!allowEdit()) return null;

      const columnTodos = todos.filter((t) => t.column_id === todo.column_id);
      const maxPosition = columnTodos.length > 0
        ? Math.max(...columnTodos.map((t) => t.position))
        : -1;
      const position = maxPosition + 1;

      const tempId = crypto.randomUUID();
      const now = new Date().toISOString();
      const optimisticTodo: Todo = {
        id: tempId,
        column_id: todo.column_id,
        title: todo.title,
        notes: todo.notes || null,
        due_date: todo.due_date || null,
        due_time: todo.due_time || null,
        duration: todo.duration ?? null,
        reminder_minutes_before: todo.reminder_minutes_before ?? null,
        reminder_fire_at: todo.reminder_fire_at ?? null,
        reminder_sent_at: null,
        assignee_id: todo.assignee_id || null,
        calendar_id: todo.calendar_id || null,
        priority: todo.priority || 'none',
        position,
        space_id: spaceId || null,
        user_id: spaceId ? null : user.id,
        archived_at: null,
        completed_at: null,
        created_by: user.id,
        created_at: now,
        updated_at: now,
      };

      // Optimistic: show immediately
      onOptimisticId?.(tempId);
      pendingOpsRef.current.add(tempId);
      setTodos((current) => [...current, optimisticTodo]);

      try {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { id: _id, completed_at: _completed_at, ...insertPayload } = optimisticTodo;
        const { data, error } = await supabase
          .from('todos')
          .insert([insertPayload])
          .select()
          .single();

        if (error) throw error;

        // Replace temp entry with real server row
        pendingOpsRef.current.add(data.id);
        setTodos((current) =>
          current.map((t) => (t.id === tempId ? data : t))
        );

        if (todo.assignee_id && todo.assignee_id !== user.id && spaceId) {
          notifyTaskAssigned({
            assigneeId: todo.assignee_id,
            actorId: user.id,
            actorName: user.user_metadata?.display_name || user.email || 'Someone',
            spaceId,
            taskTitle: todo.title,
          });
        }

        return data;
      } catch (err) {
        console.error('Error adding todo:', err);
        setError(err instanceof Error ? err.message : 'Unknown error');
        // Revert
        setTodos((current) => current.filter((t) => t.id !== tempId));
        return null;
      }
    },
    [user, spaceId, todos]
  );

  const updateTodo = useCallback(
    async (
      id: string,
      updates: Partial<Omit<Todo, 'id' | 'created_at' | 'created_by'>>
    ): Promise<boolean> => {
      if (!user) return false;
      if (!allowEdit()) return false;

      const prevTodo = todos.find(t => t.id === id);
      if (!prevTodo) return false;

      // Optimistic update
      pendingOpsRef.current.add(id);
      setTodos((current) =>
        current.map((todo) => (todo.id === id ? { ...todo, ...updates } : todo))
      );

      try {
        const { error } = await supabase
          .from('todos')
          .update({ ...updates, updated_at: new Date().toISOString() })
          .eq('id', id);

        if (error) throw error;

        if (
          updates.assignee_id &&
          updates.assignee_id !== prevTodo.assignee_id &&
          updates.assignee_id !== user.id &&
          spaceId
        ) {
          notifyTaskAssigned({
            assigneeId: updates.assignee_id,
            actorId: user.id,
            actorName: user.user_metadata?.display_name || user.email || 'Someone',
            spaceId,
            taskTitle: prevTodo.title,
          });
        }

        return true;
      } catch (err) {
        console.error('Error updating todo:', err);
        setError(err instanceof Error ? err.message : 'Unknown error');
        // Revert
        setTodos((current) =>
          current.map((todo) => (todo.id === id ? prevTodo : todo))
        );
        return false;
      }
    },
    [user, spaceId, todos]
  );

  const deleteTodo = useCallback(
    async (id: string): Promise<boolean> => {
      if (!user) return false;
      if (!allowEdit()) return false;

      const prevTodo = todos.find(t => t.id === id);

      // Optimistic remove
      pendingOpsRef.current.add(id);
      setTodos((current) => current.filter((todo) => todo.id !== id));

      try {
        const { error } = await supabase.from('todos').delete().eq('id', id);
        if (error) throw error;
        return true;
      } catch (err) {
        console.error('Error deleting todo:', err);
        setError(err instanceof Error ? err.message : 'Unknown error');
        // Revert
        if (prevTodo) setTodos((current) => [...current, prevTodo]);
        return false;
      }
    },
    [user, todos]
  );

  const moveTodo = useCallback(
    async (
      todoId: string,
      targetColumnId: string,
      targetPosition: number
    ): Promise<boolean> => {
      if (!user) return false;
      if (!allowEdit()) return false;

      const todo = todos.find((t) => t.id === todoId);
      if (!todo) return false;

      const sourceColumnId = todo.column_id;
      const isMovingWithinColumn = sourceColumnId === targetColumnId;

      // Calculate new positions
      let updatedTodos = [...todos];

      if (isMovingWithinColumn) {
        // Reorder within same column
        const columnTodos = updatedTodos
          .filter((t) => t.column_id === targetColumnId && t.id !== todoId)
          .sort((a, b) => a.position - b.position);

        // Insert at target position
        columnTodos.splice(targetPosition, 0, todo);

        // Update positions
        columnTodos.forEach((t, index) => {
          const existingTodo = updatedTodos.find((ut) => ut.id === t.id);
          if (existingTodo) {
            existingTodo.position = index;
          }
        });
      } else {
        // Move to different column
        // Update source column positions
        const sourceTodos = updatedTodos
          .filter((t) => t.column_id === sourceColumnId && t.id !== todoId)
          .sort((a, b) => a.position - b.position);

        sourceTodos.forEach((t, index) => {
          const existingTodo = updatedTodos.find((ut) => ut.id === t.id);
          if (existingTodo) {
            existingTodo.position = index;
          }
        });

        // Update target column positions
        const targetTodos = updatedTodos
          .filter((t) => t.column_id === targetColumnId)
          .sort((a, b) => a.position - b.position);

        targetTodos.splice(targetPosition, 0, { ...todo, column_id: targetColumnId });

        targetTodos.forEach((t, index) => {
          const existingTodo = updatedTodos.find((ut) => ut.id === t.id);
          if (existingTodo) {
            existingTodo.position = index;
            existingTodo.column_id = targetColumnId;
          }
        });

        // Update the moved todo
        const movedTodo = updatedTodos.find((t) => t.id === todoId);
        if (movedTodo) {
          movedTodo.column_id = targetColumnId;
          movedTodo.position = targetPosition;
        }
      }

      // Optimistic update
      setTodos(updatedTodos);

      try {
        // Update the moved todo in database
        const { error } = await supabase
          .from('todos')
          .update({
            column_id: targetColumnId,
            position: targetPosition,
            updated_at: new Date().toISOString(),
          })
          .eq('id', todoId);

        if (error) throw error;

        // Update positions of affected todos
        const affectedTodos = updatedTodos.filter(
          (t) =>
            t.column_id === targetColumnId ||
            (t.column_id === sourceColumnId && !isMovingWithinColumn)
        );

        const positionUpdates = affectedTodos.map((t) =>
          supabase
            .from('todos')
            .update({ position: t.position })
            .eq('id', t.id)
        );

        await Promise.all(positionUpdates);
        return true;
      } catch (err) {
        console.error('Error moving todo:', err);
        setError(err instanceof Error ? err.message : 'Unknown error');
        // Revert on error
        await fetchTodos().then(setTodos);
        return false;
      }
    },
    [user, todos, fetchTodos]
  );

  const archiveTodo = useCallback(
    async (id: string): Promise<boolean> => {
      if (!user) return false;
      if (!allowEdit()) return false;

      const prevTodo = todos.find(t => t.id === id);
      const archivedAt = new Date().toISOString();

      // Optimistic
      pendingOpsRef.current.add(id);
      setTodos((current) =>
        current.map((todo) => todo.id === id ? { ...todo, archived_at: archivedAt } : todo)
      );

      try {
        const { error } = await supabase
          .from('todos')
          .update({ archived_at: archivedAt })
          .eq('id', id);
        if (error) throw error;
        return true;
      } catch (err) {
        console.error('Error archiving todo:', err);
        setError(err instanceof Error ? err.message : 'Unknown error');
        if (prevTodo) setTodos((current) =>
          current.map((todo) => todo.id === id ? prevTodo : todo)
        );
        return false;
      }
    },
    [user, todos]
  );

  const unarchiveTodo = useCallback(
    async (id: string): Promise<boolean> => {
      if (!user) return false;
      if (!allowEdit()) return false;

      const prevTodo = todos.find(t => t.id === id);

      // Optimistic
      pendingOpsRef.current.add(id);
      setTodos((current) =>
        current.map((todo) => todo.id === id ? { ...todo, archived_at: null } : todo)
      );

      try {
        const { error } = await supabase
          .from('todos')
          .update({ archived_at: null })
          .eq('id', id);
        if (error) throw error;
        return true;
      } catch (err) {
        console.error('Error unarchiving todo:', err);
        setError(err instanceof Error ? err.message : 'Unknown error');
        if (prevTodo) setTodos((current) =>
          current.map((todo) => todo.id === id ? prevTodo : todo)
        );
        return false;
      }
    },
    [user, todos]
  );

  // Toggle todo completion (stays in column, sinks to bottom when completed)
  const toggleTodoComplete = useCallback(
    async (todoId: string): Promise<boolean> => {
      if (!user) return false;
      if (!allowEdit()) return false;
      const todo = todos.find((t) => t.id === todoId);
      if (!todo) return false;

      const completing = !todo.completed_at;
      const completedAt = completing ? new Date().toISOString() : null;

      // Optimistic: update completed_at and move to bottom of column if completing
      pendingOpsRef.current.add(todoId);
      if (completing) {
        const colTodos = todos.filter((t) => t.column_id === todo.column_id && !t.archived_at);
        const maxPos = colTodos.length > 0 ? Math.max(...colTodos.map((t) => t.position)) : 0;
        setTodos((current) =>
          current.map((t) => t.id === todoId ? { ...t, completed_at: completedAt, position: maxPos + 1 } : t)
        );
      } else {
        setTodos((current) =>
          current.map((t) => t.id === todoId ? { ...t, completed_at: null } : t)
        );
      }

      try {
        const { error } = await supabase
          .from('todos')
          .update({ completed_at: completedAt, updated_at: new Date().toISOString() })
          .eq('id', todoId);
        if (error) throw error;
        return true;
      } catch (err) {
        console.error('Error toggling todo completion:', err);
        setTodos((current) => current.map((t) => t.id === todoId ? todo : t));
        return false;
      }
    },
    [user, todos]
  );

  // Archive all non-archived todos in a column
  const archiveAllTodosInColumn = useCallback(
    async (columnId: string): Promise<boolean> => {
      if (!user) return false;
      if (!allowEdit()) return false;
      const toArchive = todos.filter((t) => t.column_id === columnId && !t.archived_at);
      if (toArchive.length === 0) return true;

      const archivedAt = new Date().toISOString();

      // Optimistic
      toArchive.forEach((t) => pendingOpsRef.current.add(t.id));
      setTodos((current) =>
        current.map((t) => toArchive.some((a) => a.id === t.id) ? { ...t, archived_at: archivedAt } : t)
      );

      try {
        const { error } = await supabase
          .from('todos')
          .update({ archived_at: archivedAt, updated_at: archivedAt })
          .eq('column_id', columnId)
          .is('archived_at', null);
        if (error) throw error;
        return true;
      } catch (err) {
        console.error('Error archiving column todos:', err);
        setTodos((current) =>
          current.map((t) => {
            const orig = toArchive.find((a) => a.id === t.id);
            return orig ? orig : t;
          })
        );
        return false;
      }
    },
    [user, todos]
  );

  // ============================================
  // AUTO-ARCHIVE
  // ============================================

  // Tracks the last settings key we ran auto-archive with — resets when days/enabledAt change
  const lastAutoArchiveKeyRef = useRef<string>('');

  useEffect(() => {
    if (!user || !autoArchive?.enabled || columns.length === 0 || todos.length === 0) return;

    // Re-run whenever the days threshold or enabledAt changes; skip duplicate runs for the same settings
    const runKey = `${autoArchive.days}-${autoArchive.enabledAt ?? ''}`;
    if (lastAutoArchiveKeyRef.current === runKey) return;
    lastAutoArchiveKeyRef.current = runKey;

    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - autoArchive.days);

    // Only auto-archive tasks completed after auto-archive was enabled (no retroactive archiving)
    const enabledAt = autoArchive.enabledAt ? new Date(autoArchive.enabledAt) : null;

    const toArchive = todos.filter(
      (t) =>
        !t.archived_at &&
        t.completed_at &&
        new Date(t.completed_at) < cutoff &&
        (!enabledAt || new Date(t.completed_at) >= enabledAt)
    );

    if (toArchive.length === 0) return;

    const archiveIds = toArchive.map((t) => t.id);
    const archivedAt = new Date().toISOString();

    // Batch archive in DB
    supabase
      .from('todos')
      .update({ archived_at: archivedAt })
      .in('id', archiveIds)
      .then(({ error }) => {
        if (error) {
          console.error('Auto-archive error:', error);
          lastAutoArchiveKeyRef.current = ''; // allow retry on next render
          return;
        }
        setTodos((current) =>
          current.map((t) =>
            archiveIds.includes(t.id) ? { ...t, archived_at: archivedAt } : t
          )
        );
      });
  }, [user, autoArchive, columns, todos]);

  // ============================================
  // REAL-TIME SUBSCRIPTIONS
  // ============================================

  useEffect(() => {
    if (!user) return;

    fetchAll();

    // Set up subscriptions
    const channelSuffix = spaceId ? `space_id=eq.${spaceId}` : `user_personal:${user.id}`;
    const filter = spaceId ? `space_id=eq.${spaceId}` : `user_id=eq.${user.id}`;

    // Columns subscription
    const columnsSubscription = supabase
      .channel(`kanban_columns:${channelSuffix}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'kanban_columns',
          filter,
        },
        (payload) => {
          if (payload.eventType === 'INSERT') {
            const newColumn = payload.new as KanbanColumn;
            if (spaceId === null && newColumn.space_id !== null) return;
            setColumns((current) => {
              if (current.some((c) => c.id === newColumn.id)) return current;
              return [...current, newColumn].sort((a, b) => a.position - b.position);
            });
          } else if (payload.eventType === 'UPDATE') {
            const updated = payload.new as KanbanColumn;
            setColumns((current) =>
              current
                .map((col) => (col.id === updated.id ? updated : col))
                .sort((a, b) => a.position - b.position)
            );
          } else if (payload.eventType === 'DELETE') {
            const deleted = payload.old as KanbanColumn;
            setColumns((current) => current.filter((col) => col.id !== deleted.id));
            setTodos((current) => current.filter((todo) => todo.column_id !== deleted.id));
          }
        }
      )
      .subscribe();

    // Todos subscription
    const todosSubscription = supabase
      .channel(`todos:${channelSuffix}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'todos',
          filter,
        },
        (payload) => {
          if (payload.eventType === 'INSERT') {
            const newTodo = payload.new as Todo;
            if (spaceId === null && newTodo.space_id !== null) return;
            if (newTodo.archived_at) return;
            // Skip echo of our own optimistic insert (temp ID already replaced)
            if (pendingOpsRef.current.has(newTodo.id)) {
              pendingOpsRef.current.delete(newTodo.id);
              return;
            }
            setTodos((current) => {
              if (current.some((t) => t.id === newTodo.id)) return current;
              return [...current, newTodo];
            });
          } else if (payload.eventType === 'UPDATE') {
            const updated = payload.new as Todo;
            // Skip echo of our own optimistic update
            if (pendingOpsRef.current.has(updated.id)) {
              pendingOpsRef.current.delete(updated.id);
              return;
            }
            if (updated.archived_at) {
              setTodos((current) => current.filter((todo) => todo.id !== updated.id));
            } else {
              setTodos((current) =>
                current.map((todo) => (todo.id === updated.id ? updated : todo))
              );
            }
          } else if (payload.eventType === 'DELETE') {
            const deleted = payload.old as Todo;
            // Skip echo of our own optimistic delete
            if (pendingOpsRef.current.has(deleted.id)) {
              pendingOpsRef.current.delete(deleted.id);
              return;
            }
            setTodos((current) => current.filter((todo) => todo.id !== deleted.id));
          }
        }
      )
      .subscribe();

    return () => {
      columnsSubscription.unsubscribe();
      todosSubscription.unsubscribe();
    };
  }, [user, spaceId, fetchAll]);

  // ============================================
  // RETURN
  // ============================================

  return {
    // Data
    columns,
    todos,
    loading,
    error,

    // Column operations
    addColumn,
    updateColumn,
    deleteColumn,
    reorderColumns,

    // Todo operations
    addTodo,
    updateTodo,
    deleteTodo,
    moveTodo,
    archiveTodo,
    unarchiveTodo,
    toggleTodoComplete,
    archiveAllTodosInColumn,

    // Refetch
    refetch: fetchAll,
  };
}
